import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import CheckVerifiedIcon from "@redotech/redo-icons/check-verified-01.svg";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { SetVendorAuthContext } from "@redotech/redo-marketplace-app-common/vendor-auth";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import RedoLogoSvg from "@redotech/redo-web/logo.svg";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export const CampaignVerify = memo(function CampaignVerify() {
  const { campaignSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const next = searchParams.get("next");

  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );
  const setVendorAuth = useContext(SetVendorAuthContext);

  // Load campaign data for branding/display purposes
  const campaignResult = useLoad(async () => {
    if (!campaignSlug) return null;
    return await marketplaceRpcClient.getCampaignBySlug({ slug: campaignSlug });
  }, [campaignSlug, marketplaceRpcClient]);

  const campaign = campaignResult.value?.campaign;

  const verifyResult = useLoad(async () => {
    if (!token) return null;
    // Use the existing verifyMagicLink endpoint - campaign context is in the JWT token
    return await marketplaceRpcClient.verifyMagicLink({ token });
  }, [token, marketplaceRpcClient]);

  useEffect(() => {
    if (verifyResult.value && setVendorAuth) {
      // Parse the returned auth token and set the vendor auth
      const { authToken, vendor } = verifyResult.value;

      // The auth token is a JWT that contains vendorId, email, and potentially campaignId
      // For campaign tokens, the campaign context will be extracted from the JWT
      setVendorAuth({
        vendorId: vendor.id,
        email: vendor.email,
        token: authToken,
        expiration: Temporal.Instant.fromEpochSeconds(
          // JWT tokens are typically valid for 30 days as configured
          Temporal.Now.instant().epochSeconds + 30 * 24 * 60 * 60,
        ),
      });

      // Redirect to the intended page or campaign-specific home
      const redirectTo = next ? decodeURIComponent(next) : "/";
      navigate(redirectTo, { replace: true });
    }
  }, [verifyResult.value, setVendorAuth, navigate, next]);

  if (!token) {
    return (
      <Flex
        align="center"
        className="min-h-screen bg-gray-50"
        dir="column"
        justify="center"
        px="xl"
        py="6xl"
      >
        <Flex className="sm:mx-auto sm:w-full sm:max-w-md" w="full">
          <Flex
            className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-red-200"
            dir="column"
            gap="xl"
            w="full"
          >
            <Flex
              align="center"
              className="mx-auto h-12 w-12 rounded-full bg-red-100"
              justify="center"
            >
              <XCircleIcon className="h-6 w-6 text-red-600" />
            </Flex>

            <Flex align="center" dir="column" gap="lg">
              <Text as="h1" color="gray-900" fontSize="2xl" fontWeight="bold">
                Invalid Campaign Link
              </Text>

              <Text color="gray-600" fontSize="sm" textAlign="center">
                This campaign magic link is missing a token. Please check your
                email and click the correct link for{" "}
                {campaign?.name || "the campaign"}.
              </Text>

              <RedoButton
                hierarchy="primary"
                onClick={() => navigate(`/campaign/${campaignSlug}`)}
                text="Back to campaign login"
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  if (verifyResult.pending) {
    return (
      <Flex
        align="center"
        className="min-h-screen bg-gray-50"
        dir="column"
        justify="center"
        px="xl"
        py="6xl"
      >
        <Flex className="sm:mx-auto sm:w-full sm:max-w-md" w="full">
          <Flex
            className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200"
            dir="column"
            gap="xl"
            w="full"
          >
            {/* Campaign Branding */}
            {campaign?.branding?.logoUrl ? (
              <Flex align="center" gap="lg" justify="center">
                <img
                  alt={campaign.name}
                  className="h-12 w-auto"
                  src={campaign.branding.logoUrl}
                />
                <Text color="gray-400" fontSize="2xl">
                  +
                </Text>
                <RedoLogoSvg style={{ height: "48px", width: "auto" }} />
              </Flex>
            ) : (
              <Flex justify="center">
                <RedoLogoSvg style={{ height: "48px", width: "auto" }} />
              </Flex>
            )}

            <Flex align="center" dir="column" gap="lg">
              <Text as="h1" color="gray-900" fontSize="2xl" fontWeight="bold">
                Signing you in to {campaign?.name || "Campaign"}
              </Text>

              <Text color="gray-600" fontSize="sm" textAlign="center">
                Please wait while we verify your campaign access link.
              </Text>

              <RedoSpinner size="lg" />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  if (verifyResult.error) {
    const isExpired =
      verifyResult.error.message?.includes("expired") ||
      verifyResult.error.message?.includes("invalid");

    return (
      <Flex
        align="center"
        className="min-h-screen bg-gray-50"
        dir="column"
        justify="center"
        px="xl"
        py="6xl"
      >
        <Flex className="sm:mx-auto sm:w-full sm:max-w-md" w="full">
          <Flex
            className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-red-200"
            dir="column"
            gap="xl"
            w="full"
          >
            <Flex
              align="center"
              className="mx-auto h-12 w-12 rounded-full bg-red-100"
              justify="center"
            >
              <XCircleIcon className="h-6 w-6 text-red-600" />
            </Flex>

            <Flex align="center" dir="column" gap="lg">
              <Text as="h1" color="gray-900" fontSize="2xl" fontWeight="bold">
                {isExpired ? "Campaign Link Expired" : "Campaign Access Error"}
              </Text>

              <Text color="gray-600" fontSize="sm" textAlign="center">
                {isExpired ? (
                  <>
                    This campaign magic link has expired or is invalid. Campaign
                    links are only valid for 1 hour after they're sent.
                  </>
                ) : (
                  "There was a problem accessing this campaign."
                )}
              </Text>

              <RedoAlert
                color="error"
                description={
                  verifyResult.error.message ||
                  "Unable to verify your campaign access link."
                }
                showCloseButton={false}
                title="Error Details"
              />

              {campaign && (
                <Flex dir="column" gap="md" w="full">
                  <RedoButton
                    className="w-full"
                    hierarchy="primary"
                    onClick={() => navigate(`/campaign/${campaignSlug}`)}
                    text="Request a new campaign access link"
                  />

                  {/* Check if campaign is still active */}
                  {new Date() > new Date(campaign.endTime) && (
                    <Flex
                      className="p-3 bg-yellow-50 border border-yellow-200 rounded-md"
                      w="full"
                    >
                      <Text color="yellow-800" fontSize="xs">
                        Note: This campaign ended on{" "}
                        {new Date(campaign.endTime).toLocaleDateString()}. New
                        access may no longer be available.
                      </Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  // If we get here, verification succeeded and we're redirecting
  return (
    <Flex
      align="center"
      className="min-h-screen bg-gray-50"
      dir="column"
      justify="center"
      px="xl"
      py="6xl"
    >
      <Flex className="sm:mx-auto sm:w-full sm:max-w-md" w="full">
        <Flex
          className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200"
          dir="column"
          gap="xl"
          w="full"
        >
          <Flex
            align="center"
            className="mx-auto h-12 w-12 rounded-full bg-green-100"
            justify="center"
          >
            <CheckVerifiedIcon className="h-6 w-6 text-green-600" />
          </Flex>

          <Flex align="center" dir="column" gap="lg">
            <Text as="h1" color="gray-900" fontSize="2xl" fontWeight="bold">
              Welcome to {campaign?.name || "Campaign"}!
            </Text>

            <Text color="gray-600" fontSize="sm" textAlign="center">
              You've been successfully signed in. Redirecting to your exclusive
              campaign marketplace...
            </Text>

            {campaign?.branding?.subtitle && (
              <Text color="gray-500" fontSize="xs" textAlign="center">
                {campaign.branding.subtitle}
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});
